import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import usePlacesTop from '../../hooks/usePlacesTop';
import { t } from '../../i18n';

import ArrowScrollRow from '../../components/common/ArrowScrollRow';
// ============================================
// „Darts in deiner Stadt“ (2026-09-20) – Top-Orte als Chips.
// Startseite (volle Variante, seit 2026-09-24 als HORIZONTALER Slider mit
// 60 Städten und Pfeil-Buttons – Marcel: „viel mehr Orte“, alle Links im DOM
// für SEO) und Footer (compact, 10 Städte, nur Links). Daten kommen einmal über usePlacesTop (Modul-Cache), d. h.
// keine zweite API-Last für den Footer. Leer/Fehler → rendert nichts.
// ============================================

const ACCENT = '#3fced1';

/** Scrollschritt der Pfeile (px) – etwa drei Chips */
export const CITIES_SCROLL_STEP = 360;

export interface TopCitiesSectionProps {
  /** Anzahl Städte (Startseite 60, Footer 10) */
  limit?: number;
  /** Kompakte Variante für den Footer: nur Überschrift + Links */
  variant?: 'default' | 'compact';
  /** Zusätzliche Klassen am Wurzelelement (z. B. Abstand im Footer) */
  className?: string;
}

const TopCitiesSection: React.FC<TopCitiesSectionProps> = ({ limit = 1000, variant = 'default', className }) => {
  const { places } = usePlacesTop(limit);
  if (places.length === 0) return null;

  if (variant === 'compact') {
    return (
      <div className={['min-w-0', className].filter(Boolean).join(' ')} data-testid="top-cities-compact">
        <h3 className="mb-4 text-xs font-semibold uppercase tracking-widest text-gray-500">
          {t('web.component.top-cities-section.darts-in-deiner-stadt')}
        </h3>
        <ul className="flex flex-wrap gap-x-4 gap-y-2">
          {places.map((p) => (
            <li key={p.slug}>
              <Link
                to={p.path}
                className="rounded-md text-sm text-gray-400 transition-colors hover:text-[#3fced1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
              >
                {p.name}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/darten-in"
              className="inline-flex items-center gap-1 rounded-md text-sm font-semibold text-gray-300 transition-colors hover:text-[#3fced1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
            >
              {t('web.component.top-cities-section.alle-orte')} <ArrowRight size={13} />
            </Link>
          </li>
        </ul>
      </div>
    );
  }

  return (
    <section aria-label={t('web.component.top-cities-section.darts-in-deiner-stadt')} data-testid="top-cities-section" className={className}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="section-heading text-base font-bold text-white">
            {t('web.component.top-cities-section.darts-in-deiner-stadt')}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {t('web.component.top-cities-section.vereine-spielstaetten-shops-und')}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Link
            to="/darten-in"
            className="mr-1 flex items-center gap-1 text-xs font-semibold text-gray-400 transition-colors hover:text-[#3fced1]"
          >
            {t('web.component.top-cities-section.alle-orte')} <ArrowRight size={13} />
          </Link>
        </div>
      </div>
      {/* Eine Zeile, seitlich scrollbar; alle Links bleiben im DOM (SEO) */}
      <ArrowScrollRow overlay as="ul" testId="top-cities" step={CITIES_SCROLL_STEP} compact scrollClassName="flex snap-x gap-2 pb-1">
        {places.map((p) => (
          <li key={p.slug} className="shrink-0 snap-start">
            <Link
              to={p.path}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-gray-300 transition-colors hover:border-[#3fced1]/50 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
            >
              <MapPin size={13} style={{ color: ACCENT }} className="shrink-0" />
              <span>{p.name}</span>
            </Link>
          </li>
        ))}
      </ArrowScrollRow>
    </section>
  );
};

export default TopCitiesSection;
